import React, { useState, useEffect, useCallback } from 'react';
import { getCheckinStats } from '../api';

/**
 * CheckInStats Component
 *
 * Displays two stat cards showing:
 * - Check-ins today
 * - Check-ins in the last 7 days
 *
 * Auto-refreshes every 30 seconds
 */
function CheckInStats({ refreshTrigger }) {
  const [stats, setStats] = useState({
    checkinsToday: 0,
    checkinsLast7Days: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const result = await getCheckinStats();
      if (result.success) {
        setStats({
          checkinsToday: result.checkinsToday,
          checkinsLast7Days: result.checkinsLast7Days,
        });
        setError(null);
      } else {
        setError(result.message || 'Failed to load stats');
      }
    } catch (err) {
      setError(err.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stats on mount and set up auto-refresh
  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Refresh when refreshTrigger changes (after a check-in)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchStats();
    }
  }, [refreshTrigger, fetchStats]);

  if (loading) {
    return (
      <div className="checkin-stats-grid">
        <div className="checkin-stat-card loading">
          <div className="stat-skeleton" />
        </div>
        <div className="checkin-stat-card loading">
          <div className="stat-skeleton" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkin-stats-error">
        <p>{error}</p>
        <button onClick={fetchStats} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="checkin-stats-grid">
      <div className="checkin-stat-card">
        <div className="stat-icon stat-icon-today">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <div className="stat-content">
          <span className="stat-label">Check-ins Today</span>
          <span className="stat-value">{stats.checkinsToday.toLocaleString()}</span>
        </div>
      </div>
      <div className="checkin-stat-card">
        <div className="stat-icon stat-icon-week">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <div className="stat-content">
          <span className="stat-label">Last 7 Days</span>
          <span className="stat-value">{stats.checkinsLast7Days.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

export default CheckInStats;
